import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { usePlatformAPI } from "@/api/PlatformProvider";
import { type APIError } from "@/api/APIError";
import { ErrorBox } from "@/components/ui/ErrorBox";
import { PartnerAppForm, type PartnerAppFormValues } from "./PartnerAppForm";

type PartnerApp = {
    id: string;
    name: string;
    description?: string;
    notificationEmail: string;
    scope: Record<string, unknown>;
    requireSigning: boolean;
    rollAfterDays?: number;
    rolloverDays?: number;
    codeLength: number;
    codeFormat: string;
};

function EditPartnerAppPage() {
    const { id, appId } = useParams<{ id: string; appId: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const api = usePlatformAPI();
    const appsFrom = encodeURIComponent(searchParams.get("from") ?? "/partners");
    const partnerName = searchParams.get("partnerName");
    const partnerNameParam = partnerName ? `&partnerName=${encodeURIComponent(partnerName)}` : "";
    const appsURL = `/partners/${id}?from=${appsFrom}${partnerNameParam}`;

    const [app, setApp] = useState<PartnerApp | null>(null);
    const [loadError, setLoadError] = useState<APIError | null>(null);

    useEffect(() => {
        api.get<PartnerApp>(`api/partners/${id}/apps/${appId}`).then(([data, err]) => {
            if (err) { setLoadError(err); return; }
            setApp(data);
        });
    }, [api, id, appId]);

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-row items-center gap-4">
                <button onClick={() => navigate(appsURL)} className="text-grey hover:text-dark-dark-grey flex items-center gap-1">
                    <ArrowBackIcon fontSize="small" />
                </button>
                <h1 className="flex items-center gap-2 text-xl">
                    <span className="text-grey font-medium">Apps</span>
                    <span className="text-light-grey">/</span>
                    <span className="font-semibold">Edit App</span>
                </h1>
            </div>
            <ErrorBox error={loadError} />
            {!loadError && !app && (
                <div className="flex justify-center py-12">
                    <LoadingSpinner width={32} height={32} />
                </div>
            )}
            {app && (
                <>
                    <PartnerAppForm
                        mode="edit"
                        initial={{
                            name: app.name,
                            description: app.description ?? "",
                            notificationEmail: app.notificationEmail,
                            scope: JSON.stringify(app.scope, null, 2),
                        }}
                        submitLabel="Save Changes"
                        onSubmit={async (values: PartnerAppFormValues) => {
                            const [, err] = await api.put(`api/partners/${id}/apps/${appId}`, {
                                name: values.name,
                                description: values.description || null,
                                notificationEmail: values.notificationEmail,
                                scope: JSON.parse(values.scope || "{}"),
                            });
                            if (err) throw err;
                            navigate(appsURL);
                        }}
                    />
                </>
            )}
        </div>
    );
}

export default EditPartnerAppPage;
